// components/moderntimer/index.jsx
import React, {useEffect, useMemo, useRef, useState} from 'react';

function pad(n) {
	return n < 10 ? `0${n}` : `${n}`;
}

export default function ModernTimer({
	duration = 15,
	onComplete,
	autoStart = true,
	size = 90,
	thickness = 6,
	trackColor = 'rgba(0,0,0,0.1)',
	progressColor = '#3629b5',
	warnAt = 3,
	warnColor = '#ff4d4f',
	textColor = '#fff',
	className = '',

	runKey,
}) {
	const effectiveDuration = duration;

	const r = useMemo(() => (size - thickness - 4) / 2, [size, thickness]);
	const circumference = useMemo(() => 2 * Math.PI * r, [r]);

	const [remaining, setRemaining] = useState(effectiveDuration);
	const [running, setRunning] = useState(Boolean(autoStart));
	const rafRef = useRef(null);
	const endTsRef = useRef(0);
	const didCompleteRef = useRef(false);

	const start = () => {
		didCompleteRef.current = false;
		setRemaining(effectiveDuration);
		setRunning(true);
		endTsRef.current = performance.now() + effectiveDuration * 1000;

		if (rafRef.current) {
			cancelAnimationFrame(rafRef.current);
		}
		const tick = (now) => {
			const msLeft = Math.max(0, endTsRef.current - now);
			const secs = msLeft / 1000;
			setRemaining(secs);
			if (msLeft <= 0) {
				if (!didCompleteRef.current) {
					didCompleteRef.current = true;
					setRunning(false);
					onComplete && onComplete();
				}
				return;
			}
			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);
	};

	useEffect(() => {
		if (!autoStart) {
			return;
		}
		start();
	}, [runKey, effectiveDuration, autoStart]);

	useEffect(() => {
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		};
	}, []);

	const progress = Math.min(
		1,
		Math.max(0, 1 - remaining / effectiveDuration)
	);
	const dashOffset = circumference * (1 - progress);

	const totalSecs = Math.ceil(remaining);
	const minutes = Math.floor(totalSecs / 60);
	const seconds = totalSecs % 60;
	const label = `${minutes}:${pad(seconds)}`;
	const warn = remaining <= warnAt;
	const strokeColor = warn ? warnColor : progressColor;
	return (
		<div
			className={`lit-circle lit-moderntimer ${className}`}
			role="timer"
			aria-live="polite"
			aria-valuemin={0}
			aria-valuemax={effectiveDuration}
			aria-valuenow={Math.max(0, Math.ceil(remaining))}
			style={{width: size, height: size}}
			title={label}
		>
			<svg
				className="lit-circle__interactive"
				width={size}
				height={size}
				viewBox={`0 0 ${size} ${size}`}
			>
				<g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
					<circle
						className="lit-moderntimer__track"
						cx={size / 2}
						cy={size / 2}
						r={r}
						fill="none"
						stroke={trackColor}
						strokeWidth={thickness}
						strokeLinecap="round"
					/>
					<circle
						className="lit-moderntimer__progress"
						cx={size / 2}
						cy={size / 2}
						r={r}
						fill="none"
						stroke={strokeColor}
						strokeWidth={thickness}
						strokeLinecap="round"
						style={{
							strokeDasharray: `${circumference}px`,
							strokeDashoffset: `${dashOffset}px`,
							transition: running
								? 'none'
								: 'stroke-dashoffset .2s linear',
						}}
					/>
				</g>
			</svg>

			<p className="lit-circle__text" style={{color: textColor}}>
				<span>{label}</span>
			</p>
		</div>
	);
}
